import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PortalDropdown } from "../../../components/PortalDropdown";
import { Channel, channelsService } from "../../../lib/api/channels";
import { useAlertStore } from "../../../stores/alertStore";
import { useUserChannelsStore } from "../../../stores/userChannelsStore";
import { useQueryClient } from "@tanstack/react-query";

interface ChannelContextMenuProps {
    channel: Channel;
    onDelete: (id: string, name: string) => void;
    isDeleting?: boolean;
}

const ChannelContextMenu = ({
    channel,
    onDelete,
    isDeleting,
}: ChannelContextMenuProps) => {
    const { t } = useTranslation("common");
    const { showAlert } = useAlertStore();
    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);
    const [isReloading, setIsReloading] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);


    const channelId = channel.id;
    const channelName = channel.name;

    const handleReload = async () => {
        setIsReloading(true);
        setIsOpen(false);
        try {
            const response = await channelsService.reloadChannel(channelId);
            if (response.success) {
                // Optimistically reflect the backend's new syncing state so the
                // list item shows the same loader as a freshly added channel.
                // Without this, loadChannels' merge guard keeps the prior
                // 'active' status and hides the syncing indicator.
                useUserChannelsStore.getState().updateChannel(channelId, {
                    status: 'syncing',
                    lastReloadedAt: new Date().toISOString(),
                });
                useUserChannelsStore.getState().loadChannels(true);
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            } else {
                showAlert({
                    message: response.error || t("channels.reloadFailed", "Failed to initiate reload."),
                    type: "error"
                });
            }
        } catch (error: any) {
            showAlert({
                message: error.message || t("channels.reloadFailed", "Failed to initiate reload."),
                type: "error"
            });
        } finally {
            setIsReloading(false);
        }
    };

    const isDisconnectedOrUninstalled = channel.status === 'disconnected' || channel.status === 'uninstalled';
    const canReload = !isDisconnectedOrUninstalled && (process.env.REMOVE_RESTRICTIONS === 'true' || !channel.lastReloadedAt || (Date.now() - new Date(channel.lastReloadedAt).getTime() > 30 * 60 * 1000));

    const menuItems = [];

    if (canReload) {
        menuItems.push({
            label: t("actions.reload", "Reload"),
            onClick: handleReload,
            icon: (
                <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_741f8e9e.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_741f8e9e.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
            ),
        });
    }

    menuItems.push({
        label: t("actions.delete"),
        onClick: () => {
            setIsOpen(false);
            onDelete(channelId, channelName);
        },
        icon: (
            <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_0dcd8937.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_0dcd8937.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
        ),
        className: "text-error-600 hover:bg-error-50",
    });


    return (
        <>
            <button
                ref={triggerRef}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className="p-1.5 rounded-md hover:bg-neutral-100 transition-colors text-neutral-500"
                aria-label={String(t("actions.openMenu"))}
                disabled={isDeleting || isReloading}
            >
                {isDeleting || isReloading ? (
                    <span className="animate-spin h-4 w-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_c0dfe357.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c0dfe357.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                ) : (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_98d4da83.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_98d4da83.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                )}
            </button>

            <PortalDropdown
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                triggerRef={triggerRef}
                className="w-48"
            >
                {menuItems.map((item, index) => (
                    <button
                        key={index}
                        onClick={(e) => {
                            e.stopPropagation();
                            item.onClick();
                        }}
                        className={`w-full flex items-center space-x-2 px-3 py-2 text-sm text-left hover:bg-neutral-50 transition-colors ${item.className || ""
                            }`}
                    >
                        {item.icon}
                        <span>{item.label}</span>
                    </button>
                ))}
            </PortalDropdown>
        </>
    );
};


export default ChannelContextMenu;
